import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Ticket, Sparkles, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { vouchersAPI } from '../utils/api';
import { useUsage } from '../hooks/useUsage';

// Phase 10 — Billing-page voucher redemption form
export const VoucherRedeemCard = ({ onRedeemed }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastSuccess, setLastSuccess] = useState(null);
  const { refresh } = useUsage();

  const handleRedeem = async (e) => {
    e?.preventDefault?.();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const res = await vouchersAPI.redeem(code.trim());
      const data = res.data;
      setLastSuccess(data);
      toast.success(data.message);
      setCode('');
      // Refresh usage so plan + expires_at re-render across the page
      await refresh();
      if (onRedeemed) onRedeemed(data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not redeem voucher.';
      toast.error(typeof detail === 'string' ? detail : 'Could not redeem voucher.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card data-testid="voucher-redeem-card" className="border-violet-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Ticket className="w-5 h-5 text-violet-600" />
          Have a voucher?
        </CardTitle>
        <CardDescription>
          Apply a code from your Formlio team to unlock a paid plan instantly.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleRedeem} className="flex flex-col sm:flex-row gap-2">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ENTER YOUR CODE"
            className="font-mono tracking-wider uppercase"
            data-testid="voucher-redeem-input"
            disabled={busy}
            autoComplete="off"
            maxLength={32}
          />
          <Button
            type="submit"
            disabled={busy || !code.trim()}
            data-testid="voucher-redeem-submit"
            className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shrink-0"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            {busy ? 'Applying…' : 'Redeem'}
          </Button>
        </form>

        {lastSuccess && (
          <div
            data-testid="voucher-redeem-success"
            className="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900"
          >
            <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />
            <span>
              {lastSuccess.message}
              {lastSuccess.expires_at && (
                <span className="block text-xs text-emerald-700 mt-0.5">
                  Plan active until {new Date(lastSuccess.expires_at).toLocaleDateString()}
                </span>
              )}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
